import { useState } from "react"

import { BrandInitialMonogram } from "@/components/brand-initial-monogram"
import { cn } from "@/lib/utils"

export interface BrandLogoProps {
  /** Resolved Active Storage URL for the brand logo, or `null` when no logo
   *  is attached. Comes from a controller render-prop seeded by
   *  `brand.logo.attached? ? rails_blob_url(brand.logo) : nil`. */
  logoUrl: string | null
  /** Brand display name. Used for the monogram fallback letter and as the
   *  default `alt` source. */
  name: string
  /** Tailwind chrome shared by both branches — typically size + shape
   *  (e.g. `"size-7 rounded-md"`, `"size-20 rounded-full ring-2 ring-white/15"`).
   *  Applied to the `<img>` AND merged onto the monogram tile so the layout
   *  doesn't shift between the success and fallback states. */
  className?: string
  /** Additional Tailwind classes applied ONLY to the monogram fallback
   *  (e.g. dark-hero `text-3xl text-white` overrides for `<InviteLanding>`).
   *  Composed after `className` via `cn()` so it wins on conflicts. */
  monogramClassName?: string
  /** Image `alt` attribute. Defaults to `""` (decorative — UX Spec §6) so
   *  screen-readers consume the visible brand-name text alongside, not the
   *  image. Pass a per-surface translated string when the logo stands alone
   *  (e.g. `t("invites.landing.brand_logo_alt", {brand_name})`). */
  alt?: string
  /** Stable testid base — emits THREE testids when provided (KD-3):
   *    - `${testId}` on the outer container (always present, both branches)
   *    - `${testId}-image` on the `<img>`
   *    - `${testId}-fallback` on the rendered `<BrandInitialMonogram>`
   *  Omit (`undefined`) → no testids emitted at all (no auto-generation). */
  testId?: string
}

/**
 * BrandLogo — branded chrome wrapper that renders an `<img>` for the brand
 * logo when `logoUrl` is set, swapping to a `<BrandInitialMonogram>` when
 * the URL is null OR when the image fails to load (`onError`).
 *
 * Single source of truth for the four creator-facing surfaces that show
 * brand chrome (OKS-303 PRD §1):
 *   - `pages/Public/InviteLanding.tsx`            (hero, dark variant)
 *   - `pages/Invitations/Accept.tsx`              (hero circle)
 *   - `pages/Onboarding/MinimalSignup.tsx`        (slim chrome)
 *   - `components/onboarding/onboarding-shell.tsx`+ `wizard-shell.tsx` (32px chrome)
 *
 * # State machine
 *
 *   logoUrl=null  ──→ render monogram (no `<img>`, no fetch)
 *   logoUrl="..." ──→ render `<img>`. If `onError` fires, flip
 *                     `failed=true` (one-way) and render monogram.
 *
 * The transition is one-way (no reset on URL change) so a flapping image
 * source can't oscillate. In practice the URL is stable per render of the
 * Inertia page; component remounts (route change) reset the state.
 *
 * # Accessibility (UX Spec §6)
 *
 * `alt=""` by default → image is decorative, brand-name text alongside is
 * the textual identity. `BrandInitialMonogram` keeps `aria-hidden="true"`
 * (OKS-266 contract). Per-surface override via `alt` prop when the logo
 * stands alone (no adjacent name).
 *
 * # Test surface (KD-3)
 *
 * Three stable testids when `testId` is provided. Container testid is
 * always present so Playwright can assert "logo surface visible" without
 * knowing which branch rendered.
 */
export function BrandLogo({
  logoUrl,
  name,
  className,
  monogramClassName,
  alt = "",
  testId,
}: BrandLogoProps) {
  const [failed, setFailed] = useState(false)
  const showImage = logoUrl !== null && !failed

  return (
    <span data-testid={testId} className="contents">
      {showImage ? (
        <img
          src={logoUrl}
          alt={alt}
          className={cn("object-cover", className)}
          data-testid={testId ? `${testId}-image` : undefined}
          onError={() => setFailed(true)}
        />
      ) : (
        <BrandInitialMonogram
          name={name}
          className={cn(className, monogramClassName)}
          testId={testId ? `${testId}-fallback` : undefined}
        />
      )}
    </span>
  )
}

export default BrandLogo
